"use client";

import { Search, X } from "lucide-react";
import * as React from "react";

import { FloatingInput } from "@/components/ui/floating-input";
import { cn } from "@/lib/utils";

export type SearchInputProps = Omit<
  React.ComponentProps<typeof FloatingInput>,
  "className" | "leadingIcon" | "label" | "error" | "helperText" | "size"
> & {
  /** Outer shell classes (applied to the relative wrapper). */
  containerClassName?: string;
  /** Extra classes on the FloatingInput. */
  inputClassName?: string;
  /**
   * When true (default), reserve right padding so the search field width doesn't
   * shift when the clear button appears.
   */
  reserveClearSpace?: boolean;
  /** `lg` matches the 56px labelled FloatingSelect/FloatingInput it sits beside. */
  size?: "default" | "lg";
};

function clearAsChange(onChange: React.ChangeEventHandler<HTMLInputElement> | undefined): void {
  if (!onChange) return;
  onChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
}

/**
 * Search field built on FloatingInput — leading magnifier via `leadingIcon`,
 * trailing clear-X positioned absolutely over the field's right edge.
 */
export function SearchInput({
  containerClassName,
  inputClassName,
  reserveClearSpace = true,
  size = "default",
  value,
  onChange,
  ...inputProps
}: SearchInputProps) {
  const strValue = value === undefined || value === null ? "" : String(value);
  const hasValue = strValue.length > 0;
  const reservesSpace = reserveClearSpace || hasValue;

  return (
    <div className={cn("relative", containerClassName)}>
      <FloatingInput
        {...inputProps}
        value={value}
        onChange={onChange}
        leadingIcon={<Search className="size-4" />}
        inputClassName={cn(reservesSpace && "pr-10", size === "lg" && "h-14", inputClassName)}
      />
      {hasValue && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            clearAsChange(onChange);
          }}
          className={cn(
            "absolute right-2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            size === "lg" ? "top-7" : "top-[22px]",
          )}
          aria-label="Clear search"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
